import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Button from 'Components/Atoms/Button';
import Typography from 'Components/Atoms/Typography';
import TextField from 'Components/Atoms/TextField';
import Checkbox from 'Components/Atoms/Checkbox';
import Link from 'Components/Atoms/Link';
import { ProviderDataProps } from 'Contexts/AuthContext';
import { emailRegex } from 'utils/helper';
import AuthLayout from 'App/Auth/Components/AuthLayout';
import AuthNoticeBanner from 'App/Auth/Components/AuthNoticeBanner';
import { AuthNotice, authNoticeText } from 'App/Auth/authNotice';

export type LoginProps = {
  onSignIn: (method: string, providerData?: ProviderDataProps) => Promise<void>;
  notice: AuthNotice | null;
  onRetryProfile: () => Promise<void>;
};

// Shared by /auth/login and /loginWithGastronaut: email and password are the
// default and visible right away, Google is the secondary option.
const Login = ({ onSignIn, notice, onRetryProfile }: LoginProps) => {
  const { t } = useTranslation('auth');
  // A finished password setup or reset hands over the address it was for.
  const location = useLocation<{ email?: string } | undefined>();
  const [loading, setloading] = useState(false);
  const busy = useRef(false);
  const emailInput = useRef<HTMLInputElement>(null);
  const [providerData, setproviderData] = useState<ProviderDataProps>({
    email: location.state?.email ?? '',
    password: '',
    rememberMe: false,
  });
  const [emailError, setemailError] = useState('');

  // One attempt at a time: Enter, double clicks and the Google button share it.
  const attempt = async (method: 'email' | 'google') => {
    if (busy.current) return;
    busy.current = true;
    setloading(true);
    try {
      await onSignIn(method, { ...providerData, email: providerData.email.trim() });
    } finally {
      busy.current = false;
      setloading(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!emailRegex(providerData.email.trim())) {
      setemailError('Please enter a valid email address.');
      emailInput.current?.focus();
      return;
    }
    attempt('email');
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;
    setemailError(value && !emailRegex(value.trim()) ? 'Please enter a valid email address.' : '');
  };

  const status = notice && (
    <AuthNoticeBanner
      title={notice.kind === 'profile-missing' ? t('No Gastronaut access for this account') : undefined}
      message={t(authNoticeText(notice))}
      focus
    >
      {notice.kind === 'profile-missing' && (
        <>
          {notice.provider === 'google' && (
            <Button variant="outlined" fullWidth translation="auth" disabled={loading} onClick={() => attempt('google')}>
              Choose another Google account
            </Button>
          )}
          <Button variant="transparent" fullWidth translation="auth" onClick={() => emailInput.current?.focus()}>
            Sign in with email and password
          </Button>
          {notice.provider === 'google' && (
            <Typography variant="text-3" color="subdued" translation="auth" block>
              No access yet? Please ask your restaurant for an invitation.
            </Typography>
          )}
        </>
      )}
      {notice.kind === 'profile-unavailable' && (
        <Button variant="outlined" fullWidth translation="auth" onClick={onRetryProfile}>
          Try again
        </Button>
      )}
    </AuthNoticeBanner>
  );

  return (
    <AuthLayout title="Login Now" subtitle="See your dashboard and manage everything!" status={status}>
      <form onSubmit={handleEmailSubmit} noValidate>
        <TextField
          paper
          value={providerData.email}
          onBlur={handleEmailBlur}
          errorMessage={emailError}
          helperTextTranslation="auth"
          onChange={(e) => {
            const email = e.target.value;
            setproviderData((pD) => ({ ...pD, email }));
            setemailError('');
          }}
          inputRef={emailInput}
          marginBottom="md"
          fullWidth
          variant="filled"
          label="Email"
          labelTranslation="auth"
          size="small"
          type="email"
          name="email"
          autoComplete="username"
          inputProps={{ autoCapitalize: 'none', spellCheck: false }}
        />
        <TextField
          paper
          value={providerData.password}
          onChange={(e) => {
            const password = e.target.value;
            setproviderData((pD) => ({ ...pD, password }));
          }}
          marginBottom="md"
          fullWidth
          variant="filled"
          label="Password"
          labelTranslation="auth"
          size="small"
          type="password"
          name="password"
          autoComplete="current-password"
        />
        <div className="auth-form-row">
          <Checkbox
            color="primary"
            checked={!!providerData.rememberMe}
            translation="auth"
            onChange={(e) => {
              const rememberMe = e.target.checked;
              setproviderData((pD) => ({ ...pD, rememberMe }));
            }}
            label="Stay signed in on this device"
          />
          <Link
            linkTo="/auth/forgot-password"
            translation="auth"
            variant="text-2"
            style={{ whiteSpace: 'nowrap' }}
          >
            Forgot Password?
          </Link>
        </div>
        <Button
          loading={loading}
          disabled={loading}
          variant="primary"
          fullWidth
          type="submit"
          translation="auth"
        >
          Login Now
        </Button>
      </form>

      <div className="auth-or-divider">
        <Typography color="subdued" variant="text-3" translation="auth">
          or
        </Typography>
      </div>

      <Button
        variant="outlined"
        onClick={() => attempt('google')}
        disabled={loading}
        startIcon={() => <img alt="" src="/Icons/google.svg" style={{ marginRight: 16 }} />}
        fullWidth
        translation="auth"
      >
        Sign in with Google
      </Button>

      <div className="auth-secondary-links">
        <Typography translation="auth" variant="text-2">
          Not registered yet?
        </Typography>
        <Link
          href="https://get.gastronaut.ai"
          translation="auth"
          variant="text-2"
          style={{ whiteSpace: 'nowrap' }}
        >
          Become a part of Gastronaut!
        </Link>
      </div>
    </AuthLayout>
  );
};

export default Login;
